import React, { useState, useMemo, useRef, useEffect } from 'react';
import { DownloadOutlined, MenuOutlined } from '@ant-design/icons';
import { Trash2 } from 'lucide-react';
import { Dropdown, Menu } from 'antd';
import { Button } from './ui/button';
import { GraphParameterSelector, GraphParameter } from './ui/GraphParameterSelector';
import { convertWeldDataValue, getParameterUnitSuffix, getUnitFromSettings } from '../utils/unitUtils';
import { formatHeatInputLabel } from '../utils/weldParameterLabels';
import {
  Chart as ChartJS,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip as ChartTooltip,
  Legend,
  ChartOptions,
  ChartData,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import {
  GRAPH_TILT_AXIS_TITLE,
  buildTiltDegreeTickValues,
  computeTiltAxisBounds,
  formatTiltLabel,
  unwrapTilts,
} from '../utils/graphTiltAxis';

// Register Chart.js components
ChartJS.register(
  LinearScale,
  PointElement,
  LineElement,
  Title,
  ChartTooltip,
  Legend
);

interface ViewGraphProps {
  detailData: any[];
  projectNumber?: string;
  projectName?: string;
}

interface GraphConfig {
  id: string;
  parameter: string;
  enabled: boolean;
}

const ViewGraph: React.FC<ViewGraphProps> = ({
  detailData,
  projectNumber = '',
  projectName = '',
}) => {
  const [graphConfigs, setGraphConfigs] = useState<GraphConfig[]>([
    { id: '1', parameter: 'travel_speed', enabled: true },
    { id: '2', parameter: 'volts', enabled: true },
  ]);

  const processedDataRef = useRef<{
    xDegrees: number[];
    graphData: Record<string, number[]>;
  }>({
    xDegrees: [],
    graphData: {},
  });

  // DOM ready check to prevent chart rendering issues
  const [isDomReady, setIsDomReady] = useState(false);
  
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsDomReady(true);
    }, 100);
    
    return () => clearTimeout(timer);
  }, []);

  // Get dynamic unit labels for parameters
  const getUnitLabel = (parameter: string): string => {
    return getParameterUnitSuffix(parameter);
  };

  // Only distance-based weld parameters need unit conversion (matches table views)
  const CONVERTIBLE_GRAPH_PARAMETERS = new Set([
    'travel_speed',
    'wire_speed',
    'oscillation_width',
    'total_wire_consumed',
    'heat',
    'true_energy',
  ]);

  const toConvertWeldParamName = (parameter: string): string => {
    switch (parameter) {
      case 'oscillation_width':
        return 'oscillationWidth';
      case 'travel_speed':
        return 'travelSpeed';
      case 'wire_speed':
        return 'wireSpeed';
      case 'true_energy':
        return 'trueEnergy';
      case 'total_wire_consumed':
        return 'totalWireConsumed';
      default:
        return parameter;
    }
  };

  // Available parameters with their display names and colors
  const availableParameters: GraphParameter[] = [
    { value: 'travel_speed', label: `Travel Speed (${getUnitLabel('travel_speed')})`, color: '#EB1C37' },
    { value: 'volts', label: `Volts (${getUnitLabel('volts')})`, color: '#FFA500' },
    { value: 'current', label: `Current (${getUnitLabel('current')})`, color: '#ee82ee' },
    { value: 'wire_speed', label: `Wire Speed (${getUnitLabel('wire_speed')})`, color: '#800080' },
    { value: 'oscillation_width', label: `Oscillate Width (${getUnitLabel('oscillation_width')})`, color: '#A52A2A' },
    { value: 'target', label: `Target (${getUnitLabel('target')})`, color: '#808080' },
    { value: 'horizontal_bias', label: `Horizontal Bias (${getUnitLabel('horizontal_bias')})`, color: '#00FFFF' },
    { value: 'frequency', label: `Frequency (${getUnitLabel('frequency')})`, color: '#FFC0CB' },
    { value: 'total_wire_consumed', label: `Total Wire Consumed (${getUnitLabel('total_wire_consumed')})`, color: '#FFFF00' },
    { value: 'true_energy', label: `True Energy (${getUnitLabel('true_energy')})`, color: '#0000FF' },
    { value: 'heat', label: formatHeatInputLabel(getUnitLabel('heat')), color: '#008000' },
    { value: 'arc_impedance', label: `Arc Impedance (${getUnitLabel('arc_impedance')})`, color: '#FF6347' },
  ];

  // Process data for charts — unwrap tilt so 357° plots as −3° on a linear X axis
  const processedData = useMemo(() => {
    if (!detailData || detailData.length === 0) {
      return { xDegrees: [], graphData: {}, tiltAxis: computeTiltAxisBounds([]) };
    }

    try {
      const rawTilts = detailData.map((item, index) => {
        const tiltValue = item.tilt ?? item.tilt_range ?? item.tiltRange ?? item.range;
        const n = typeof tiltValue === 'number' ? tiltValue : Number(tiltValue);
        return Number.isFinite(n) ? n : index + 1;
      });
      const xDegrees = unwrapTilts(rawTilts);
      const tiltAxis = computeTiltAxisBounds(xDegrees.filter((t) => Number.isFinite(t)));

      const graphData: Record<string, number[]> = {};

      availableParameters.forEach(param => {
        const data = detailData.map(item => {
          const value = item[param.value];
          const numericValue = typeof value === 'number' ? value : parseFloat(value) || 0;
          if (CONVERTIBLE_GRAPH_PARAMETERS.has(param.value)) {
            return convertWeldDataValue(numericValue, toConvertWeldParamName(param.value));
          }
          return numericValue;
        });

        // Keep length aligned with xDegrees (do not filter — index must match)
        if (data.some(value => value !== null && value !== undefined && !isNaN(value))) {
          graphData[param.value] = data;
        }
      });

      return { xDegrees, graphData, tiltAxis };
    } catch (error) {
      return { xDegrees: [], graphData: {}, tiltAxis: computeTiltAxisBounds([]) };
    }
  }, [detailData]);

  useEffect(() => {
    processedDataRef.current = {
      xDegrees: processedData.xDegrees,
      graphData: processedData.graphData,
    };
  }, [processedData]);

  // Handle parameter selection — sync configs to unique selected parameters (one graph each)
  const handleParameterChange = (selected: string[]) => {
    const uniqueSelected = Array.from(new Set(selected));

    setGraphConfigs(prev => {
      const existingByParam = new Map(prev.map(config => [config.parameter, config]));
      return uniqueSelected.map((parameter, index) => {
        const existing = existingByParam.get(parameter);
        if (existing) return existing;
        return {
          id: `${parameter}-${index}`,
          parameter,
          enabled: true,
        };
      });
    });
  };

  // Remove graph
  const removeGraph = (id: string) => {
    setGraphConfigs(prev => prev.filter(config => config.id !== id));
  };

  // Toggle graph visibility
  const toggleGraph = (id: string) => {
    setGraphConfigs(prev => prev.map(config => 
      config.id === id 
        ? { ...config, enabled: !config.enabled }
        : config
    ));
  };

  // Download CSV for a specific graph (unwrapped tilt X)
  const downloadCSV = (parameter: string) => {
    const { xDegrees, graphData } = processedDataRef.current;
    const data = graphData[parameter];
    const paramLabel = availableParameters.find(p => p.value === parameter)?.label || parameter;

    if (!xDegrees.length || !data) {
      return;
    }

    const headers = [GRAPH_TILT_AXIS_TITLE, paramLabel];
    const rows = xDegrees.map((x, index) => [formatTiltLabel(x), data[index]]);
    
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.join(',')),
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute(
      'download',
      `${projectNumber}_${projectName}_${paramLabel}.csv`
    );
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Get chart data for a specific parameter — linear { x, y } with unwrapped tilt
  const getChartData = (parameter: string): ChartData<'line'> => {
    const data = processedData.graphData[parameter] || [];
    const paramLabel = availableParameters.find(p => p.value === parameter)?.label || parameter;
    const paramColor = availableParameters.find(p => p.value === parameter)?.color || '#000000';

    const points = processedData.xDegrees.map((x, i) => ({
      x,
      y: data[i] ?? null,
    }));

    return {
      datasets: [{
        label: paramLabel,
        data: points as any,
        borderColor: paramColor,
        backgroundColor: paramColor + '20',
        borderWidth: 2,
        pointRadius: 3,
        pointHoverRadius: 6,
        pointBackgroundColor: paramColor,
        pointBorderColor: '#ffffff',
        pointBorderWidth: 2,
        tension: 0.1,
        fill: false,
        parsing: false as const,
      }],
    };
  };

  // Chart options — linear unwrapped tilt X
  const getChartOptions = (parameter: string): ChartOptions<'line'> => {
    const paramLabel = availableParameters.find(p => p.value === parameter)?.label || parameter;
    const { tiltAxis } = processedData;
    
    return {
      responsive: true,
      maintainAspectRatio: false,
      animation: {},
      plugins: {
        legend: {
          display: true,
          position: 'top' as const,
          labels: {
            color: '#374151',
            font: {
              size: 12,
            },
            usePointStyle: true,
            pointStyle: 'circle',
          },
        },
        title: {
          display: true,
          text: paramLabel,
          color: '#374151',
          font: {
            size: 14,
            weight: 'bold',
          },
        },
        tooltip: {
          enabled: true,
          mode: 'index' as const,
          intersect: false,
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          titleColor: '#ffffff',
          bodyColor: '#ffffff',
          borderColor: '#e5e7eb',
          borderWidth: 1,
          cornerRadius: 8,
          displayColors: true,
          callbacks: {
            title: (items) => {
              const x = items[0]?.parsed?.x;
              if (typeof x !== 'number' || !Number.isFinite(x)) return '';
              return `Tilt: ${formatTiltLabel(x)}°`;
            },
          },
        },
      },
      interaction: {
        mode: 'nearest' as const,
        axis: 'x' as const,
        intersect: false,
      },
      elements: {
        point: {
          hoverRadius: 6,
        },
      },
      scales: {
        x: {
          type: 'linear' as const,
          min: tiltAxis.min,
          max: tiltAxis.max,
          bounds: 'data' as const,
          display: true,
          title: {
            display: true,
            text: GRAPH_TILT_AXIS_TITLE,
            color: '#374151',
          },
          grid: {
            display: true,
            color: '#e5e7eb',
          },
          ticks: {
            color: '#374151',
            stepSize: 15,
            includeBounds: false,
            autoSkip: false,
            callback: (value) => formatTiltLabel(Number(value)),
          },
          afterBuildTicks: (axis) => {
            const ticks = buildTiltDegreeTickValues({
              dataMin: tiltAxis.dataMin,
              dataMax: tiltAxis.dataMax,
              axisMax: tiltAxis.max,
            });
            axis.ticks = ticks.map((v) => ({ value: v }));
          },
        },
        y: {
          display: true,
          title: {
            display: true,
            text: paramLabel,
            color: '#374151',
          },
          grid: {
            display: true,
            color: '#e5e7eb',
          },
          ticks: {
            color: '#374151',
          },
        },
      },
    };
  };

  // Get chart menu
  const getChartMenu = (parameter: string) => (
    <Menu>
      <Menu.Item 
        key="download" 
        icon={<DownloadOutlined />}
        onClick={() => downloadCSV(parameter)}
      >
        Download CSV
      </Menu.Item>
    </Menu>
  );

  if (!detailData || detailData.length === 0) {
    return (
      <div className="flex items-center justify-center h-64 bg-gray-50 rounded-lg">
        <div className="text-center">
          <div className="text-gray-500 text-lg mb-2">No data available</div>
          <div className="text-gray-400 text-sm">Please select a pass to view graph data</div>
        </div>
      </div>
    );
  }

  // Filter enabled graphs that have data
  const enabledGraphsWithData = graphConfigs
    .filter(config => config.enabled)
    .map(config => {
      const param = availableParameters.find(p => p.value === config.parameter);
      const data = processedData.graphData[config.parameter];
      return {
        ...config,
        param,
        hasData: data && data.some(value => value !== 0)
      };
    })
    .filter(graph => graph.param && graph.hasData);

  return (
    <div className="view-graph">
      {/* Parameter Selection */}
      <div className="mb-6">
        <GraphParameterSelector
          parameters={availableParameters}
          selected={graphConfigs.map(config => config.parameter)}
          onChange={handleParameterChange}
          title="Select Graph Parameters to Display"
          availableData={Object.fromEntries(
            availableParameters.map(param => [
              param.value, 
              processedData.graphData[param.value] && processedData.graphData[param.value].some(value => value !== 0)
            ])
          )}
        />
      </div>

      {/* Graphs Grid - 2 per row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {enabledGraphsWithData.map((graph) => (
          <div
            key={graph.id}
            className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden"
          >
            <div className="p-4 border-b border-gray-200 bg-gray-50">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div
                    className="w-3 h-3 rounded-full"
                    style={{ backgroundColor: graph.param?.color }}
                  />
                  <span className="font-medium text-gray-800">
                    {graph.param?.label}
                  </span>
                </div>
                <div className="flex items-center gap-1">
                  <Dropdown
                    overlay={getChartMenu(graph.parameter)}
                    trigger={['click']}
                    placement="bottomRight"
                  >
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-gray-500 hover:text-gray-700"
                    >
                      <MenuOutlined />
                    </Button>
                  </Dropdown>
                  
                  {graphConfigs.length > 1 && (
                    <Button
                      onClick={() => removeGraph(graph.id)}
                      variant="destructive"
                      size="sm"
                      className="p-2"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </div>
            </div>
            
            <div className="p-4">
              <div className="h-64">
                {!isDomReady ? (
                  <div className="flex items-center justify-center h-full">
                    <div className="text-gray-500">Preparing charts...</div>
                  </div>
                ) : (
                  <Line data={getChartData(graph.parameter)} options={getChartOptions(graph.parameter)} />
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {enabledGraphsWithData.length === 0 && (
        <div className="flex items-center justify-center h-64 bg-gray-50 rounded-lg">
          <div className="text-center">
            <div className="text-gray-500 text-lg mb-2">No graph data available</div>
            <div className="text-gray-400 text-sm">
              No parameters have valid data to display
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ViewGraph;
